import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import Layout from "./Layout";
import Navbar from "./NavBar";
import usePermissions from "../../hooks/usePermissions";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getLastView } from "../../utils/viewPreference";
import "./Layout.css";

/** Direct URL to a module the role can't open. */
export const NoAccess = () => (
  <div className="container-fluid py-5 px-lg-4">
    <div className="approval-empty mx-auto access-state">
      <i className="bi bi-shield-lock"></i>
      <strong className="d-block fs-100 text-navy mb-1">No access</strong>
      Your role can't open this page.{" "}
      <Link to="/dashboard">Back to the dashboard</Link>
    </div>
  </div>
);

/** Unknown URL (inside the layout). */
export const NotFoundPage = () => (
  <Layout>
    <div className="container-fluid py-5 px-lg-4">
      <div className="approval-empty mx-auto access-state">
        <i className="bi bi-signpost"></i>
        <strong className="d-block fs-100 text-navy mb-1">
          Page not found
        </strong>
        This page doesn't exist.{" "}
        <Link to="/dashboard">Back to the dashboard</Link>
      </div>
    </div>
  </Layout>
);

/** While /permissions/me loads: no menus yet (we don't know which view applies). */
const FullPageLoading = () => (
  <div className="layout-root">
    <Navbar />
    <div className="layout-body">
      <main className="layout-main">
        <div className="text-center py-5">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      </main>
    </div>
  </div>
);

/**
 * No HR access and no linked employee: nothing to show. Only the top bar
 * (profile with change password, logout) and an explanation.
 */
export const NotLinkedPage = () => (
  <div className="layout-root">
    <Navbar />
    <div className="layout-body">
      <main className="layout-main">
        <div className="container-fluid py-5 px-lg-4">
          <div className="approval-empty mx-auto access-state">
            <i className="bi bi-person-lock"></i>
            <strong className="d-block fs-100 text-navy mb-1">
              Your account isn't linked to an employee yet
            </strong>
            Ask HR to link your account to your employee record. You can still
            change your password from your profile (top right).
          </div>
        </div>
      </main>
    </div>
  </div>
);

/** Where an HR address sends someone without HR access. */
const myViewPathFor = (pathname) =>
  pathname === "/attendance" ? "/me/attendance" : "/me";

/**
 * HR view route guard, from GET /permissions/me (strict).
 * - not logged in → /login?returnUrl=…
 * - no HR access → My view (/me), or the not-linked page without an employee
 * - `module` / `any` (one of several) must be viewable, or modifiable with action="modify";
 *   company: true also needs COMPANY scope (HR-level pages). No module = any HR user.
 */
const RequireAccess = ({
  module,
  any,
  company = false,
  action = "view",
  children,
}) => {
  const user = useCurrentUser();
  const perms = usePermissions();
  const { pathname, search } = useLocation();
  if (!user.userId)
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(pathname + search)}`}
        replace
      />
    );
  if (perms.isLoading) return <FullPageLoading />;
  if (perms.isError) return <Layout />;
  if (!perms.hasHrAccess)
    return perms.employeeId ? (
      <Navigate to={myViewPathFor(pathname)} replace />
    ) : (
      <NotLinkedPage />
    );
  const modules = any || (module ? [module] : []);
  const ok =
    modules.length === 0 ||
    modules.some((m) =>
      company ? perms.companyWide(m, action) : perms.can(m, action),
    );
  return ok ? (
    children
  ) : (
    <Layout>
      <NoAccess />
    </Layout>
  );
};

/**
 * My view route guard: needs a linked employee. HR accounts without one go
 * to HR view; others see the not-linked page.
 */
export const RequireEmployee = ({ children }) => {
  const user = useCurrentUser();
  const perms = usePermissions();
  const { pathname, search } = useLocation();
  if (!user.userId)
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(pathname + search)}`}
        replace
      />
    );
  if (perms.isLoading) return <FullPageLoading />;
  if (!perms.employeeId)
    return perms.hasHrAccess ? (
      <Navigate to="/dashboard" replace state={{ notLinked: true }} />
    ) : (
      <NotLinkedPage />
    );
  return children;
};

/**
 * "/" after login: HR users get their last view (default HR view), employees
 * My view, accounts with neither the not-linked page.
 */
export const HomeRedirect = () => {
  const user = useCurrentUser();
  const perms = usePermissions();
  if (!user.userId) return <Navigate to="/login" replace />;
  if (perms.isLoading) return <FullPageLoading />;
  if (perms.hasHrAccess) {
    const wantsMe = perms.employeeId && getLastView(user.userId) === "me";
    return <Navigate to={wantsMe ? "/me" : "/dashboard"} replace />;
  }
  return perms.employeeId ? <Navigate to="/me" replace /> : <NotLinkedPage />;
};

export default RequireAccess;
